import { createContext, useState, useCallback, useMemo } from "react";

import { Navigate } from "react-router-dom";

import { RVRoutingPage } from "@regal-voice/ui";
import { usePageAvailability } from "App/routes/Hooks/usePageAvilability/usePageAvailability";
import { AppPagePaths } from "App/routes/paths";
import { AIAgentsSubNav } from "Pages/agentBuilder/Components/AIAgentsSubnav/AIAgentsSubnav";

export type TestAIAgentConnectionDetails = {
    token: string;
    wsUrl: string;
};

export type TestAIAgentConnections = {
    [key: string]: TestAIAgentConnectionDetails;
};

export const AgentTestPanelContext = createContext({
    connections: {} as TestAIAgentConnections,
    setConnection: (_key: string, _value: TestAIAgentConnectionDetails | null) => {},
    getConnection: (_key: string): TestAIAgentConnectionDetails | null => null,
    clearConnections: () => {},
});

export default function AIAgentsRoutingPage(): JSX.Element {
    const { showAIAgents } = usePageAvailability();

    const [connections, setConnections] = useState<TestAIAgentConnections>({});

    const setConnection = useCallback((key: string, value: TestAIAgentConnectionDetails | null) => {
        setConnections((prev) => {
            if (value === null) {
                const newConnections = { ...prev };
                delete newConnections[key];
                return newConnections;
            }
            return {
                ...prev,
                [key]: value,
            };
        });
    }, []);

    const getConnection = useCallback(
        (key: string): TestAIAgentConnectionDetails | null => {
            return connections[key] || null;
        },
        [connections]
    );

    const clearConnections = useCallback(() => {
        setConnections({});
    }, []);

    const testPanelContextValue = useMemo(
        () => ({
            connections,
            setConnection,
            getConnection,
            clearConnections,
        }),
        [connections, setConnection, getConnection, clearConnections]
    );

    if (!showAIAgents) {
        return <Navigate to={AppPagePaths.fallback} />;
    }

    return (
        <AgentTestPanelContext.Provider value={testPanelContextValue}>
            <RVRoutingPage nav={<AIAgentsSubNav />} />
        </AgentTestPanelContext.Provider>
    );
}
